import {useState} from "react";
import type {trpc} from "@/app/trpc";
import {ShadowBox, XStack, YStack, DashedBadge, Separator, Button} from "@chayka/ui";
import {transformSlots} from "@/utils/format";
import {capitalizeFirstLetter, MONTH_DAY_FORMATTER, WEEKDAY_FORMATTER_LONG} from "@chayka/utils";
import {BookingDetailsBottomSheet} from "../BookingDetailsBottomSheet";
import {CancelBookingBottomSheet} from "../CancelBookingBottomSheet";

interface BookingCardProps {
  booking: (typeof trpc.booking.list)["~types"]["output"][number];
  withMoreButton?: boolean;
}

export const BookingCard: React.FC<BookingCardProps> = ({booking, withMoreButton}) => {
  if (!booking.roomData) {
    throw new Error("Нет данных о комнате в бронировании");
  }

  const [isDetailsBottomSheetOpen, setIsDetailsBottomSheetOpen] = useState(false);
  const [isCancelBottomSheetOpen, setIsCancelBottomSheetOpen] = useState(false);

  const date = new Date(booking.date);
  const hoursFormatted = transformSlots(booking.slots.map((slot) => slot.hour));
  const weekday = capitalizeFirstLetter(WEEKDAY_FORMATTER_LONG.format(date));
  const dateFormatted = MONTH_DAY_FORMATTER.format(date).replace(".", "");

  return (
    <>
      <ShadowBox className="rounded-lg p-4">
        <YStack className="gap-4">
          <XStack className="justify-between items-start">
            <YStack className="text-sm gap-2">
              <div>{weekday}</div>
              <div>{hoursFormatted}</div>
            </YStack>
            <DashedBadge className="text-xs">{booking.roomData?.name}</DashedBadge>
          </XStack>
          <XStack className="justify-between items-end">
            <div className="text-5xl uppercase font-light">{dateFormatted}</div>
            <div className="text-lg font-bold">{booking.price} ₽</div>
          </XStack>
          <Separator />
          <XStack className="w-full gap-2">
            {withMoreButton && (
              <Button
                size="small"
                className="flex flex-1 justify-center"
                onClick={() => setIsDetailsBottomSheetOpen(true)}
              >
                Подробнее
              </Button>
            )}
            <Button
              variant="negative"
              size="small"
              className="flex flex-1 justify-center"
              onClick={() => setIsCancelBottomSheetOpen(true)}
            >
              Отменить
            </Button>
          </XStack>
        </YStack>
      </ShadowBox>
      {withMoreButton && (
        <BookingDetailsBottomSheet
          booking={booking}
          isOpen={isDetailsBottomSheetOpen}
          onClose={() => setIsDetailsBottomSheetOpen(false)}
        />
      )}
      <CancelBookingBottomSheet
        bookingIds={booking.slots.map((slot) => slot.id)}
        isOpen={isCancelBottomSheetOpen}
        onClose={() => setIsCancelBottomSheetOpen(false)}
      />
    </>
  );
};
